import React, { useState, useEffect, useRef } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PageLayout from "../../components/PageLayout";
import StepFlowNavigate from "../../components/StepFlowNavigate";
import { selectGAClientId } from "../../features/analytics/analyticsSlice";
import useCampaignAttribution from "../../hooks/useCampaignAttribution";

import QuoteBuyingProperty from "../QuoteCommon/QuoteBuyingProperty";
import QuoteSellingProperty from "../QuoteCommon/QuoteSellingProperty";
import QuoteSorryPage from "../QuoteCommon/QuoteSorryPage";

import ReferrerAgentAndClientDetails from "./ReferrerAgentAndClientDetails";
import ReferrerQuoteTransactionType from "./ReferrerQuoteTransactionType";
import ReferrerQuoteSentPage from "./ReferrerQuoteSentPage";
import ReferrerAnythingElse from "./ReferrerAnythingElse";
import ReferrerQuoteDetailsPage from "./ReferrerQuoteDetailsPage";
import ReferrerQuoteConnells from "./ReferrerQuoteConnells";
import ReferrerQuoteSubmission from "./ReferrerQuoteSubmission";
import { isConnellsBranch, parseInternalConnellsBranchCode } from "./utils/ReferrerQuoteUtils";
import useSignedInEstateAgent from "./hooks/useSignedInEstateAgent";

const transactionTypeOptions = [
  { title: "Buying", value: "Buying" },
  { title: "Selling", value: "Selling" },
  { title: "Buying and selling at the same time", value: "BuyingAndSelling" },
];

// The Connells in-branch screen keeps its original option titles.
const connellsTransactionTypeOptions = [
  { title: "Buying a property", value: "Buying" },
  { title: "Selling a property", value: "Selling" },
  { title: "Buying and selling at the same time", value: "BuyingAndSelling" },
];

// Fallback channel to use for pricing if no prices are configured for the
// channel loaded from the referral code or URL params etc.
const fallbackChannel = "estate_agent_referrals";

const ReferrerQuoteFlow = (props) => {
  let { "*": referralCodeRaw } = useParams();

  const cleanupReferralCode = (referralCodeParam) => {
    if (!referralCodeParam) {
      return referralCodeParam;
    }
    // if referral code contains < (%3c) or / then we should trim it as it's likely a corrupted url
    return referralCodeParam.split(/[</]/, 2)[0];
  };
  const referralCode = cleanupReferralCode(referralCodeRaw);
  const referralLocked = !!referralCode;

  const location = useLocation();
  const navigate = useNavigate();

  // FIXME: move this into components that use it once they've been converted
  // to functional components.
  const googleAnalyticsId = useSelector(selectGAClientId);
  const campaign = useCampaignAttribution();

  const [state, setState] = useState({
    loading: !!referralCode,
    error: null,
    referral: null,
    defaultChannel: "estate_agent_referrals",
    defaultReferrer: null,
    inBranchReferral: false,
    isInternalReferralLink: false,
  });

  const [flowCompleted, setFlowCompleted] = useState(false);
  const [submittedFlowState, setSubmittedFlowState] = useState(null);
  const [agentName, setAgentName] = useState(null);
  const [agentEmailAddress, setAgentEmailAddress] = useState(null);
  // A signed-in estate agent's details are filled in for them. Resolved in the background; the
  // details step is a few steps in, and adopts a late answer while its fields are still empty.
  const signedInAgent = useSignedInEstateAgent();

  const lastFetchedReferralCode = useRef(null);

  useEffect(() => {
    const branchId = parseInternalConnellsBranchCode(referralCode);
    if (!!branchId) {
      setState((prevState) => ({
        ...prevState,
        loading: false,
        defaultReferrer: branchId,
        inBranchReferral: true,
        isInternalReferralLink: true,
      }));
      return;
    }
    if (isConnellsBranch(referralCode)) {
      setState((prevState) => ({
        ...prevState,
        loading: false,
        defaultReferrer: referralCode,
        inBranchReferral: true,
      }));
      return;
    }

    if (!!referralCode) {
      if (lastFetchedReferralCode.current === referralCode) return;
      lastFetchedReferralCode.current = referralCode;

      fetch(`/api/referralCode/${referralCode}?audience=referrer`)
        .then((resp) => resp.json())
        .then(
          (referralData) => {
            if (referralData.referralCodeValid) {
              const referral = {
                referralCode: referralCode,
                discountAmount: referralData.discountAmount,
                discountMessage: referralData.discountMessage,
                quoteMessage: referralData.quoteMessage,
                referralSource: referralData.referralSource,
              };

              setState((prevState) => ({
                ...prevState,
                loading: false,
                referral: referral,
              }));
            } else {
              if (process.env.NODE_ENV === "development") {
                console.warn(
                  `DEV: referral code "${referralCode}" not found. Referral codes are synced from webapp-alpha. ` +
                    "Run ./scripts/run_manage_command.sh resync_referral_codes in webapp-alpha to populate them locally.",
                );
              }
              setState((prevState) => ({
                ...prevState,
                loading: false,
                referral: null,
                defaultReferrer: referralCode,
                inBranchReferral: false,
              }));
            }
          },
          (err) => {
            console.error("Failed to load referral code", err);
            setState((prevState) => ({ ...prevState, loading: false }));
          },
        );
    }
  }, [referralCode]);

  const shouldShowBuyingPropertyDetails = (transactionType) => {
    return transactionType === "Buying" || transactionType === "BuyingAndSelling";
  };

  const shouldShowSellingPropertyDetails = (transactionType) => {
    return transactionType === "Selling" || transactionType === "BuyingAndSelling";
  };

  if (state.loading) {
    return null;
  }

  if (flowCompleted) {
    return (
      <PageLayout>
        <ReferrerQuoteSentPage
          buyingDetails={submittedFlowState.buyingDetails}
          defaultChannel={state.defaultChannel}
          inBranchReferral={state.inBranchReferral}
          recipient="client"
          referral={submittedFlowState?.referral}
          quoteDetails={submittedFlowState.quoteDetails}
          transactionType={submittedFlowState.transactionType}
          sellingDetails={submittedFlowState.sellingDetails}
          restartFlow={() => {
            setSubmittedFlowState(null);
            setFlowCompleted(false);
            navigate(location.pathname);
          }}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <StepFlowNavigate
        initialFlowState={{
          transactionType: null,
          referral: state.referral,
          personalDetails: null,
          sellingDetails: null,
          sellingRejectionReasons: [],
          buyingDetails: null,
          buyingRejectionReasons: [],
          additionalInformation: null,
          agentName: agentName || null,
          agentEmailAddress: agentEmailAddress || null,
        }}
        steps={[
          {
            component: (flowState) => {
              if (state.inBranchReferral === true) {
                return (
                  <ReferrerQuoteConnells
                    transactionType={flowState.transactionType}
                    transactionTypeOptions={connellsTransactionTypeOptions}
                    referral={flowState.referral}
                    referralLocked={referralLocked}
                    defaultReferrer={state.defaultReferrer}
                    isInternalReferralLink={state.isInternalReferralLink}
                  />
                );
              } else {
                return (
                  <ReferrerQuoteTransactionType
                    transactionType={flowState.transactionType}
                    transactionTypeOptions={transactionTypeOptions}
                    referral={flowState.referral}
                  />
                );
              }
            },
            name: "Referrer quote intro",
            slug: "refer-a-client",
          },
          {
            component: (flowState) => (
              <QuoteSellingProperty
                mode="referrer"
                transactionType={flowState.transactionType}
                sellingDetails={flowState.sellingDetails}
                defaultChannel={state.defaultChannel}
                referral={flowState.referral}
                referralLocked={referralLocked}
                hideReferralCodeBanner={state.inBranchReferral}
              />
            ),
            name: "Referrer Selling property details",
            valid: (flowState) => shouldShowSellingPropertyDetails(flowState.transactionType),
            slug: "sale-property-details",
          },
          {
            component: (flowState) => (
              <QuoteSorryPage
                mode="referrer"
                transactionType={flowState.transactionType}
                rejectionReasons={flowState.sellingRejectionReasons}
                rejectQuote="selling"
              />
            ),
            name: "Selling sorry page",
            valid: (flowState) => flowState.sellingRejectionReasons.length > 0,
            slug: "sale-sorry",
          },
          {
            component: (flowState) => (
              <QuoteBuyingProperty
                mode="referrer"
                transactionType={flowState.transactionType}
                buyingDetails={flowState.buyingDetails}
                defaultChannel={state.defaultChannel}
                referral={flowState.referral}
                referralLocked={referralLocked}
                hideReferralCodeBanner={state.inBranchReferral}
              />
            ),
            name: "Referrer Buying property details",
            valid: (flowState) => shouldShowBuyingPropertyDetails(flowState.transactionType),
            slug: "purchase-details",
          },
          {
            component: (flowState) => (
              <QuoteSorryPage
                mode="referrer"
                transactionType={flowState.transactionType}
                rejectionReasons={flowState.buyingRejectionReasons}
                rejectQuote="buying"
              />
            ),
            name: "Buying sorry page",
            valid: (flowState) => flowState.buyingRejectionReasons.length > 0,
            slug: "purchase-sorry",
          },
          {
            component: (flowState) => (
              <ReferrerAgentAndClientDetails
                agentName={flowState.agentName || signedInAgent?.fullName || null}
                agentEmailAddress={flowState.agentEmailAddress || signedInAgent?.emailAddress || null}
                additionalInformation={flowState.additionalInformation}
                personalDetails={flowState.personalDetails}
              />
            ),
            name: "Referrer quote agent and client details page",
            slug: "referrer-and-client-details",
          },
          {
            component: (flowState) => (
              <ReferrerAnythingElse
                transactionType={flowState.transactionType}
                additionalInformation={flowState.additionalInformation}
                showTermsLink={true}
                buttonText="See the price"
              />
            ),
            name: "Referrer Anything else",
            slug: "anything-else",
          },
          {
            component: (flowState) => (
              <ReferrerQuoteDetailsPage
                transactionType={flowState.transactionType}
                defaultChannel={state.defaultChannel}
                fallbackChannel={fallbackChannel}
                sellingDetails={flowState.sellingDetails}
                buyingDetails={flowState.buyingDetails}
                referral={flowState.referral}
                hideReferralCodeBanner={state.inBranchReferral}
                googleAnalyticsId={googleAnalyticsId}
                campaign={campaign}
              />
            ),
            name: "Referrer quote partial quote page",
            slug: "quote-details",
          },
          {
            component: (flowState) => (
              <ReferrerQuoteSubmission
                transactionType={flowState.transactionType}
                defaultChannel={state.defaultChannel}
                fallbackChannel={fallbackChannel}
                defaultReferrer={state.defaultReferrer}
                agentName={flowState.agentName}
                agentEmailAddress={flowState.agentEmailAddress}
                additionalInformation={flowState.additionalInformation}
                personalDetails={flowState.personalDetails}
                sellingDetails={flowState.sellingDetails}
                buyingDetails={flowState.buyingDetails}
                recipient="client"
                referral={flowState.referral}
                inBranchReferral={state.inBranchReferral}
                googleAnalyticsId={googleAnalyticsId}
                campaign={campaign}
                onFlowComplete={(quoteDetails) => {
                  setSubmittedFlowState({
                    ...flowState,
                    ...quoteDetails,
                  });
                  setAgentName(flowState.agentName);
                  setAgentEmailAddress(flowState.agentEmailAddress);
                  setFlowCompleted(true);
                }}
              />
            ),
            name: "Referrer quote submission",
            slug: "quote-sent",
          },
        ]}
      />
    </PageLayout>
  );
};

export default ReferrerQuoteFlow;
